/**
 * Shared chart styling constants used by all ERAX chart components.
 * Centralizes fonts, colors, axis config, and formatting rules.
 */

export const CHART_STYLE = {
  fontSize: {
    tick: 10,
    axisTitle: 11,
    dataLabel: 9,
    chartTitle: 13,
    legend: 10,
  },
  colors: [
    '#4F46E5', // indigo-600
    '#7C3AED', // violet-600
    '#2563EB', // blue-600
    '#0891B2', // cyan-600
    '#059669', // emerald-600
    '#D97706', // amber-600
    '#DC2626', // red-600
    '#4B5563', // gray-600
  ],
  xAxis: {
    angle: -45,
    textAnchor: 'end' as const,
    height: 52,
    maxLabelLength: 12,
    tickFill: '#6B7280',
  },
  yAxis: {
    width: 48,
    tickFill: '#6B7280',
  },
  margin: { top: 8, right: 8, bottom: 52, left: 48 },
  /** Hide data labels on bars if the dataset has more items than this. */
  dataLabelThreshold: 15,
  /** For line charts: only show min/max/last labels above this count. */
  lineLabelStrategy: 30,
  tooltip: {
    fontSize: 11,
    padding: '6px 10px',
    borderRadius: 8,
    border: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
} as const;

/** Truncate a label to maxLen characters, appending "..." if truncated. */
export const truncateLabel = (label: string, maxLen = CHART_STYLE.xAxis.maxLabelLength): string =>
  typeof label === 'string' && label.length > maxLen
    ? `${label.slice(0, maxLen)}...`
    : String(label ?? '');

/** Compact number formatting for y-axis ticks: 1000 → 1K, 1000000 → 1M. */
export const formatAxisNumber = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) return '';
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toString();
};

/**
 * Smart data value formatter that adapts decimal places based on the
 * nature of the values in the column.
 *
 * @param value - The numeric value to format
 * @param hasDecimals - Whether the column contains any non-integer values
 * @param allSmall - Whether all values in the column are < 1
 */
export const formatDataValue = (
  value: number,
  hasDecimals = false,
  allSmall = false,
): string => {
  if (typeof value !== 'number' || isNaN(value)) return '';
  if (!hasDecimals) return value.toLocaleString();
  if (allSmall) return value.toFixed(2);
  return value.toFixed(1);
};

/**
 * Analyze a numeric column to determine decimal formatting strategy.
 * Returns { hasDecimals, allSmall } to pass to formatDataValue.
 */
export const analyzeDecimals = (data: Record<string, unknown>[], key: string) => {
  const values = data
    .map((row) => row[key])
    .filter((v): v is number => typeof v === 'number' && !isNaN(v));
  const hasDecimals = values.some((v) => !Number.isInteger(v));
  const allSmall = hasDecimals && values.every((v) => Math.abs(v) < 1);
  return { hasDecimals, allSmall };
};
